import React, { useEffect, useState } from 'react'
import { Badge, Button, Card, Col, Dropdown, Row, Spinner, Table } from 'react-bootstrap'
import { Link, useParams } from 'react-router-dom'
import { API_BASE_URL, API_BASE_URL_Main, getBearerToken, getLoadingState, setLoadingState } from '../../../functions';
import axios from 'axios';
import PDFPreview from '../../PDFPreview';
import verticalicon from '../.././../images/verticalicon.svg';
import ToastMessage from '../../ToastMessage';
import { useAuth } from '../auth/AuthProvider';
import toast from 'react-hot-toast';
import ConfirmationPopup from '../../Modals/ConfirmationPopup';
import UmrahTimeUpdate from '../../Modals/UmrahTimeUpdate';
import UmrahConfirmBooking from '../../Modals/UmrahConfirmBooking';
import UmrahUpdatePNR from '../../Modals/UmrahUpdatePNR';
import UmrahAdditionalDiscount from '../../Modals/UmrahAdditionalDiscount';
import UmrahCustomPricing from '../../Modals/UmrahCustomPricing';
import BookUmrah from '../../Modals/BookUmrah';

const UmrahBookingDetail = () => {
  const { id } = useParams();
  const [bookingData, setBookingData] = useState()
  const [isLoading, setIsLoading] = useState(true)
  const [showToast, setShowToast] = useState('show')
  const [toastMessage, setToastMessage] = useState(null);
  const { token } = useAuth()
  const [targetBooking, setTargetBooking] = useState('')
  const [showConfirm, setShowConfirm] = useState(false)
  const [showTime, setShowTime] = useState(false)
  const [showPNR, setShowPNR] = useState(false)
  const [showDiscount, setShowDiscount] = useState(false)
  const [showCustomPricing, setShowCustomPricing] = useState(false)
  const [showEdit, setShowEdit] = useState(false)
  const [confirmation, setConfirmation] = useState({ show: false, target: '' })
  const [pdfLoading, setPdfLoading] = useState({ download: false, view: false })

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [])


  // Helper function to determine if package has via flights
  const isViaFlight = () => {
    const departureDirection = bookingData?.umrah_package?.departure_direction;
    const returnDirection = bookingData?.umrah_package?.return_direction;
    
    return departureDirection === 'via' || returnDirection === 'via';
  };

  // Helper function to get airline name from flight object
  const getAirlineName = (flight) => {
    if (!flight || !flight.airline) return 'Unknown Airline';
    
    const airline = flight.airline;
    return airline.full_name || airline.short_name || airline.name || 'Unknown Airline';
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  // Helper function to format time
  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    try {
      // Handle different time formats
      if (typeof timeString === 'string') {
        // If it's already a formatted time string, return as is
        if (timeString.includes('AM') || timeString.includes('PM')) {
          return timeString;
        }
        
        // If it's a datetime string, extract time part
        if (timeString.includes('T')) {
          const timePart = timeString.split('T')[1].split('.')[0];
          return formatTimeToAMPM(timePart);
        }
        
        // If it's in HH:MM format, format it properly
        if (timeString.match(/^\d{1,2}:\d{2}$/)) {
          return formatTimeToAMPM(timeString);
        }
      }
      
      return formatTimeToAMPM(timeString);
    } catch (error) {
      console.error('Error formatting time:', timeString, error);
      return 'N/A';
    }
  };

  // Helper function to convert time to AM/PM format
  const formatTimeToAMPM = (timeString) => {
    if (!timeString) return 'N/A';
    
    try {
      // Handle HH:MM format
      if (timeString.match(/^\d{1,2}:\d{2}$/)) {
        const [hours, minutes] = timeString.split(':');
        const hour = parseInt(hours, 10);
        const minute = parseInt(minutes, 10);
        
        if (isNaN(hour) || isNaN(minute)) return 'N/A';
        
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        
        return `${displayHour}:${minute.toString().padStart(2, '0')} ${ampm}`;
      }
      
      return timeString;
    } catch (error) {
      console.error('Error converting time to AM/PM:', timeString, error);
      return 'N/A';
    }
  };

  // Helper function to get flight route
  const getFlightRoute = (flights) => {
    if (!flights || flights.length === 0) return 'N/A';
    const firstFlight = flights[0];
    const lastFlight = flights[flights.length - 1];
    return `${firstFlight.departure_airport} → ${lastFlight.arrival_airport}`;
  };

  // Helper function to get flight duration
  const getFlightDuration = (flights) => {
    if (!flights || flights.length === 0) return 'N/A';
    const firstFlight = flights[0];
    const lastFlight = flights[flights.length - 1];
    const departure = new Date(`${firstFlight.departure_date} ${firstFlight.departure_time}`);
    const arrival = new Date(`${lastFlight.arrival_date} ${lastFlight.arrival_time}`);
    const duration = arrival - departure;
    const hours = Math.floor(duration / (1000 * 60 * 60));
    const minutes = Math.floor((duration % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  // Fetch booking details
  useEffect(() => {
    const fetchBookingDetails = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/umrah-bookings/${id}`, {
          headers: { Authorization: `Bearer ${getBearerToken()}` }
        });
        console.log('Booking Data:', response.data.data);
        setBookingData(response.data.data);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching booking details:', error);
        setIsLoading(false);
        toast.error('Failed to fetch booking details');
      }
    };

    if (id) {
      fetchBookingDetails();
    }
  }, [id]);

  const handleCancel = (booking) => {
    setTargetBooking(booking);
    setConfirmation({ show: true, target: 'cancel' });
  };

  const handleEdit = (booking) => {
    setTargetBooking(booking);
    setShowEdit(true);
  };

  const handleDownloadPDF = async () => {
    setPdfLoading(prev => ({ ...prev, download: true }));
    
    try {
      const url = `${API_BASE_URL}/umrah-booking-pdf/${id}`;
      console.log('Download PDF URL:', url);
      console.log('Bearer Token:', getBearerToken());
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${getBearerToken()}`,
          'Accept': 'application/pdf'
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const pdfUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = `umrah-booking-${id}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(pdfUrl);
        toast.success('PDF downloaded successfully');
      } else {
        throw new Error('Network response was not ok');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    } finally {
      setPdfLoading(prev => ({ ...prev, download: false }));
    }
  };

  const handleViewPDF = async () => {
    setPdfLoading(prev => ({ ...prev, view: true }));
    
    try {
      const url = `${API_BASE_URL}/umrah-booking-pdf-view/${id}`;
      console.log('View PDF URL:', url);
      console.log('Bearer Token:', getBearerToken());
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${getBearerToken()}`,
          'Accept': 'application/pdf'
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const pdfUrl = window.URL.createObjectURL(blob);
        window.open(pdfUrl, '_blank');
        toast.success('PDF opened successfully');
      } else {
        throw new Error('Network response was not ok');
      }
    } catch (error) {
      console.error('Error viewing PDF:', error);
      toast.error('Failed to view PDF');
    } finally {
      setPdfLoading(prev => ({ ...prev, view: false }));
    }
  };

  const handleConfirmAction = async () => {
    if (confirmation.target === 'cancel') {
      const tid = toast.loading('Cancelling booking...');
      
      try {
        await axios.post(`${API_BASE_URL}/umrah-bookings/${targetBooking.id}/cancel`, {}, {
          headers: { Authorization: `Bearer ${getBearerToken()}` }
        });
        
        toast.success('Booking cancelled successfully');
        toast.dismiss(tid);
        setConfirmation({ show: false, target: '' });
        
        // Refresh booking data
        const response = await axios.get(`${API_BASE_URL}/umrah-bookings/${id}`, {
          headers: { Authorization: `Bearer ${getBearerToken()}` }
        });
        setBookingData(response.data.data);
      } catch (error) {
        toast.error(error.response?.data?.message || 'Failed to cancel booking');
        toast.dismiss(tid);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1">Umrah Booking Details</h4>
          <p className="text-muted mb-0">Complete booking information and management</p>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <Button 
            variant="success" 
            size="sm"
            onClick={handleDownloadPDF}
            disabled={pdfLoading.download}
            style={{
              backgroundColor: '#28a745',
              borderColor: '#28a745',
              color: 'white',
              fontSize: '12px',
              padding: '6px 12px',
              borderRadius: '4px',
              border: '1px solid #28a745',
              transition: 'all 0.2s ease',
              fontWeight: '500'
            }}
            onMouseEnter={(e) => {
              if (!pdfLoading.download) {
                e.target.style.backgroundColor = '#218838';
                e.target.style.borderColor = '#1e7e34';
              }
            }}
            onMouseLeave={(e) => {
              if (!pdfLoading.download) {
                e.target.style.backgroundColor = '#28a745';
                e.target.style.borderColor = '#28a745';
              }
            }}
          >
            {pdfLoading.download ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
              </>
            ) : (
              <>
                <i className="fas fa-download me-1"></i>
                Download PDF
              </>
            )}
          </Button>
          <Dropdown>
            <Dropdown.Toggle 
              variant="outline-primary" 
              id="dropdown-basic"
              style={{
                borderColor: '#e9ecef',
                color: '#6c757d',
                backgroundColor: 'transparent'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f8f9fa';
                e.target.style.borderColor = '#dee2e6';
                e.target.style.color = '#495057';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.borderColor = '#e9ecef';
                e.target.style.color = '#6c757d';
              }}
            >
              <img src={verticalicon} alt="options" />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {token.role === 'admin' ? (
                <>
                  <Link to="#" className="dropdown-item" onClick={handleDownloadPDF}>
                    <i className="fas fa-download me-2"></i>
                    Download PDF
                  </Link>
                  <Link to="#" className="dropdown-item" onClick={handleViewPDF}>
                    <i className="fas fa-eye me-2"></i>
                    View PDF
                  </Link>
                  <Link to="#" className="dropdown-item" onClick={() => { setTargetBooking(bookingData); setShowTime(true); }}>
                    Update Time
                  </Link>
                  <Link to="#" className="dropdown-item" onClick={() => { setTargetBooking(bookingData); setShowConfirm(true); }}>
                    Confirm Booking
                  </Link>
                  <Link to="#" className="dropdown-item" onClick={() => { setTargetBooking(bookingData); setShowPNR(true); }}>
                    Update PNR
                  </Link>
                  <Link to="#" className="dropdown-item" onClick={() => { setTargetBooking(bookingData); setShowDiscount(true); }}>
                    Additional Discount
                  </Link>
                  <Link to="#" className="dropdown-item" onClick={() => { setTargetBooking(bookingData); setShowCustomPricing(true); }}>
                    Edit Custom Pricing
                  </Link>
                </>
              ) : (
                <>
                  <Link to="#" className="dropdown-item" onClick={handleDownloadPDF}>
                    <i className="fas fa-download me-2"></i>
                    Download PDF
                  </Link>
                  <Link to="#" className="dropdown-item" onClick={handleViewPDF}>
                    <i className="fas fa-eye me-2"></i>
                    View PDF
                  </Link>
                  {bookingData?.confirmed !== '1' && bookingData?.cancel_status !== '1' && (
                    <>
                      <Link to="#" className="dropdown-item" onClick={() => handleEdit(bookingData)}>
                        <i className="fas fa-edit me-2"></i>
                        Edit Booking
                      </Link>
                      <Link to="#" className="dropdown-item" onClick={() => handleCancel(bookingData)}>
                        <i className="fas fa-times me-2"></i>
                        Cancel Booking
                      </Link>
                    </>
                  )}
                </>
              )}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      <Row>
        {!bookingData ? (
          <div className={'mt-3 w-100 text-center'}>
            <Spinner as='span' animation='border' size='sm' role='status' aria-hidden='true' />
          </div>
        ) : (
          <Col className='m-auto mt-3' xxl={12} xl={12} lg={12}>
            {/* Booking Header */}
            <div className="main_heading d-flex align-items-center mb-4">
              <div className="d-block">
                <p className='m-0 fontsize16'> 
                  <strong>Booking Date:</strong> {formatDate(bookingData?.created_at)}
                </p>
                <small className='d-flex m-0'>
                  {bookingData?.user && (
                    <p className='m-0 P-0 me-2'>
                      <span><strong>Booked By:</strong></span> {bookingData?.user?.name}
                    </p>
                  )}
                </small>
              </div>
            </div>

            {/* 1. Booking Information Section */}
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Booking Information</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <p><strong>Booking ID:</strong> <Badge bg="secondary">{bookingData?.id}</Badge></p>
                      <p><strong>Package:</strong> {bookingData?.umrah_package?.title}</p>
                      <p><strong>PNR:</strong> {bookingData?.pnr || 'Not Assigned'}</p>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <p><strong>Booking Status:</strong> 
                        <Badge
                          bg=""
                          className={`p-2 py-1 ms-2 ${(bookingData?.confirmed == 1 && bookingData?.cancel_status == 0) ? 'successcolorbg whitetext' :
                            (bookingData?.confirmed == 0 && bookingData?.cancel_status == 0) ? 'warninglighterbg yellowtext' :
                              bookingData?.cancel_status == 1 && 'errorlighterbg redtext'}`}
                        >
                          {bookingData?.cancel_status == 1 ? 'Cancelled' :
                            bookingData?.confirmed == 1 ? 'Confirmed' :
                              bookingData?.confirmed == 0 && 'Hold'}
                        </Badge>
                      </p>
                      <p><strong>Payment Status:</strong> 
                        <Badge
                          bg=""
                          className={`p-2 py-1 ms-2 ${bookingData?.payment_status == '2' ? 'successcolorbg whitetext' : bookingData?.payment_status == '1' ? 'warninglighterbg yellowtext' : bookingData?.payment_status == '0' && 'errorlighterbg redtext'}`}
                        >
                          {bookingData?.payment_status == '2' ? 'Paid' : bookingData?.payment_status == '1' ? 'Partial' : bookingData?.payment_status == '0' && 'Unpaid'}
                        </Badge>
                      </p>
                      {(bookingData?.cancel_status != 1 && bookingData?.hold_expires_at) && (
                        <p><strong>Hold Expires:</strong> {new Date(bookingData?.hold_expires_at).toLocaleString()}</p>
                      )}
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* 2. Pricing Information Section */}
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Pricing Information</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <div className="mb-3">
                      <p><strong>Room Type:</strong> <Badge bg="info" className="text-capitalize">{bookingData?.room_type}</Badge></p>
                      <p><strong>Number of Rooms:</strong> <Badge bg="primary">{bookingData?.room_quantity} {bookingData?.room_quantity === 1 ? 'Room' : 'Rooms'}</Badge></p>
                      <p><strong>Total Passengers:</strong> {bookingData?.number_of_passengers}</p>
                      <p>
                        {bookingData?.number_of_adults > 0 && <span><strong>Adults:</strong> {bookingData.number_of_adults}</span>}
                        {bookingData?.number_of_children > 0 && <span>{bookingData?.number_of_adults > 0 ? ' • ' : ''}<strong>Children:</strong> {bookingData.number_of_children}</span>}
                        {bookingData?.number_of_infants > 0 && <span>{(bookingData?.number_of_adults > 0 || bookingData?.number_of_children > 0) ? ' • ' : ''}<strong>Infants:</strong> {bookingData.number_of_infants}</span>}
                      </p>
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <div className="p-3 bg-light rounded">
                        {/* Custom Pricing Indicator */}
                        {bookingData?.is_custom_pricing && (
                          <div className="mb-3">
                            <Badge bg="warning" className="mb-2">
                              <i className="fas fa-edit me-1"></i>
                              Custom Pricing Applied
                            </Badge>
                            <hr />
                          </div>
                        )}
                        
                        <p><strong>Subtotal:</strong> PKR {bookingData?.total_amount?.toLocaleString()}</p>
                        {bookingData?.adult_discount_amount > 0 && (
                          <p className="text-success"><strong>Adult Discount:</strong> -PKR {bookingData?.adult_discount_amount?.toLocaleString()}</p>
                        )}
                        {bookingData?.child_discount_amount > 0 && (
                          <p className="text-success"><strong>Child Discount:</strong> -PKR {bookingData?.child_discount_amount?.toLocaleString()}</p>
                        )}
                        {bookingData?.infant_discount_amount > 0 && (
                          <p className="text-success"><strong>Infant Discount:</strong> -PKR {bookingData?.infant_discount_amount?.toLocaleString()}</p>
                        )}
                        {(bookingData?.adult_discount_amount > 0 || bookingData?.child_discount_amount > 0 || bookingData?.infant_discount_amount > 0) && (
                          <p className="text-success"><strong>Total Package Discount:</strong> -PKR {bookingData?.package_discount_amount?.toLocaleString()}</p>
                        )}
                        {bookingData?.additional_discount_amount > 0 && (
                          <p className="text-success">
                            <strong>Additional Discount:</strong> -PKR {bookingData?.additional_discount_amount?.toLocaleString()}
                            {bookingData?.additional_discount_name && (
                              <span className="text-muted"> ({bookingData.additional_discount_name})</span>
                            )}
                          </p>
                        )}
                        <hr />
                        <h5 className="text-success mb-0"><strong>Final Amount: PKR {bookingData?.final_amount?.toLocaleString()}</strong></h5>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* 3. Passenger Details Section */}
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Passenger Details</h5>
              </Card.Header>
              <Card.Body>
                <Table responsive>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Passport No.</th>
                      <th>Date of Birth</th>
                      <th>Gender</th>
                      <th>Nationality</th>
                      <th>Passport</th>
                      <th>CNIC</th>
                      <th>Picture</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookingData?.passengers?.map((passenger, index) => (
                      <tr key={index}>
                        <td>
                          <div>
                            <strong>{passenger.full_name}</strong>
                            {passenger.remarks && (
                              <small className="text-muted d-block">{passenger.remarks}</small>
                            )}
                          </div>
                        </td>
                        <td>
                          <Badge bg={passenger.passenger_type === 'adult' ? 'primary' : passenger.passenger_type === 'child' ? 'warning' : 'info'}>
                            {passenger.passenger_type?.toUpperCase()}
                          </Badge>
                        </td>
                        <td>{passenger.passport_number || 'N/A'}</td>
                        <td>{passenger.date_of_birth ? formatDate(passenger.date_of_birth) : 'N/A'}</td>
                        <td>{passenger.gender || 'N/A'}</td>
                        <td>{passenger.nationality || 'N/A'}</td>
                        <td>
                          {passenger.passport_img ? (
                            <Link to={`${API_BASE_URL_Main}/public/${passenger.passport_img}`} target='_blank'>View</Link>
                          ) : 'N/A'}
                        </td>
                        <td>
                          {passenger.cnic_picture ? (
                            <Link to={`${API_BASE_URL_Main}/public/${passenger.cnic_picture}`} target='_blank'>View</Link>
                          ) : 'N/A'}
                        </td>
                        <td>
                          {passenger.picture ? (
                            <Link to={`${API_BASE_URL_Main}/public/${passenger.picture}`} target='_blank'>View</Link>
                          ) : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>

            {/* 4. Flight Information Section */}
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Flight Information</h5>
              </Card.Header>
              <Card.Body>

                {/* Flight Information - Direct Flights (Combined) */}
                {!isViaFlight() && bookingData?.umrah_package?.departure_flights && bookingData.umrah_package.departure_flights.length > 0 && (
                  <div className="mb-4">
                    <div className="p-3 bg-light rounded">
                      <Row>
                        {/* Departure Flight */}
                        {bookingData.umrah_package.departure_flights.map((flight, idx) => (
                          <Col md={6} key={`dep-${idx}`} className="mb-3">
                            <div className="p-3 border rounded h-100" style={{ backgroundColor: 'white' }}>
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <h6 className="mb-0 text-primary">
                                  {!isViaFlight() ? 'DEPARTURE' : `DEPARTURE - VIA ${idx + 1}`}
                                </h6>
                                <small className="text-muted">
                                  {getAirlineName(flight)} - {flight.flight_number}
                                </small>
                              </div>
                              
                              <div className="mb-2">
                                <div className="d-flex align-items-center">
                                  <i className="fas fa-plane-departure text-info me-2"></i>
                                  <div>
                                    <strong>{flight.departure_airport}</strong>
                                    <br />
                                    <small className="text-muted">
                                      {formatDate(flight.departure_date)} {formatTime(flight.departure_time)}
                                    </small>
                                  </div>
                                </div>
                                
                                <div className="d-flex align-items-center my-2">
                                  <div className="me-2" style={{ width: '24px' }}></div>
                                  <div className="text-center">
                                    <i className="fas fa-arrow-down text-muted"></i>
                                    <small className="text-muted d-block">to</small>
                                  </div>
                                </div>
                                
                                <div className="d-flex align-items-center">
                                  <i className="fas fa-plane-arrival text-success me-2"></i>
                                  <div>
                                    <strong>{flight.arrival_airport}</strong>
                                    <br />
                                    <small className="text-muted">
                                      {formatDate(flight.arrival_date)} {formatTime(flight.arrival_time)}
                                    </small>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="border-top pt-2">
                                <div className="row">
                                  {flight.departure_terminal && (
                                    <div className="col-6">
                                      <small className="text-muted">Departure Terminal:</small><br />
                                      <strong>{flight.departure_terminal}</strong>
                                    </div>
                                  )}
                                  {flight.arrival_terminal && (
                                    <div className="col-6">
                                      <small className="text-muted">Arrival Terminal:</small><br />
                                      <strong>{flight.arrival_terminal}</strong>
                                    </div>
                                  )}
                                </div>
                                
                                <div className="row mt-2">
                                  {flight.baggage_allowance && (
                                    <div className="col-6">
                                      <small className="text-muted">Baggage:</small><br />
                                      <strong>{flight.baggage_allowance}</strong>
                                    </div>
                                  )}
                                  {flight.hand_carry && (
                                    <div className="col-6">
                                      <small className="text-muted">Hand Carry:</small><br />
                                      <strong>{flight.hand_carry}</strong>
                                    </div>
                                  )}
                                </div>
                                
                                {flight.meals_included && (
                                  <div className="row mt-2">
                                    <div className="col-12">
                                      <div className="d-flex align-items-center">
                                        <i className="fas fa-utensils text-success me-2"></i>
                                        <small className="text-success fontweight600">Meals Included</small>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </Col>
                        ))}
                        
                        {/* Return Flight - Only for Direct Flights */}
                        {bookingData.umrah_package.return_flights && bookingData.umrah_package.return_flights.length > 0 && (
                          bookingData.umrah_package.return_flights.map((flight, idx) => (
                            <Col md={6} key={`ret-${idx}`} className="mb-3">
                              <div className="p-3 border rounded h-100" style={{ backgroundColor: 'white' }}>
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <h6 className="mb-0 text-primary">
                                    RETURN
                                  </h6>
                                  <small className="text-muted">
                                    {getAirlineName(flight)} - {flight.flight_number}
                                  </small>
                                </div>
                                
                                <div className="mb-2">
                                  <div className="d-flex align-items-center">
                                    <i className="fas fa-plane-departure text-info me-2"></i>
                                    <div>
                                      <strong>{flight.departure_airport}</strong>
                                      <br />
                                      <small className="text-muted">
                                        {formatDate(flight.departure_date)} {formatTime(flight.departure_time)}
                                      </small>
                                    </div>
                                  </div>
                                  
                                  <div className="d-flex align-items-center my-2">
                                    <div className="me-2" style={{ width: '24px' }}></div>
                                    <div className="text-center">
                                      <i className="fas fa-arrow-down text-muted"></i>
                                      <small className="text-muted d-block">to</small>
                                    </div>
                                  </div>
                                  
                                  <div className="d-flex align-items-center">
                                    <i className="fas fa-plane-arrival text-success me-2"></i>
                                    <div>
                                      <strong>{flight.arrival_airport}</strong>
                                      <br />
                                      <small className="text-muted">
                                        {formatDate(flight.arrival_date)} {formatTime(flight.arrival_time)}
                                      </small>
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="border-top pt-2">
                                  <div className="row">
                                    {flight.departure_terminal && (
                                      <div className="col-6">
                                        <small className="text-muted">Departure Terminal:</small><br />
                                        <strong>{flight.departure_terminal}</strong>
                                      </div>
                                    )}
                                    {flight.arrival_terminal && (
                                      <div className="col-6">
                                        <small className="text-muted">Arrival Terminal:</small><br />
                                        <strong>{flight.arrival_terminal}</strong>
                                      </div>
                                    )}
                                  </div>
                                  
                                  <div className="row mt-2">
                                    {flight.baggage_allowance && (
                                      <div className="col-6">
                                        <small className="text-muted">Baggage:</small><br />
                                        <strong>{flight.baggage_allowance}</strong>
                                      </div>
                                    )}
                                    {flight.hand_carry && (
                                      <div className="col-6">
                                        <small className="text-muted">Hand Carry:</small><br />
                                        <strong>{flight.hand_carry}</strong>
                                      </div>
                                    )}
                                  </div>
                                  
                                  {flight.meals_included && (
                                    <div className="row mt-2">
                                      <div className="col-12">
                                        <div className="d-flex align-items-center">
                                          <i className="fas fa-utensils text-success me-2"></i>
                                          <small className="text-success fontweight600">Meals Included</small>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </Col>
                          ))
                        )}
                      </Row>
                    </div>
                  </div>
                )}

                {/* Via Flights - Separate Sections */}
                {isViaFlight() && bookingData?.umrah_package?.departure_flights && bookingData.umrah_package.departure_flights.length > 0 && (
                  <div className="mb-4">
                    <h6 className="text-warning mb-3">
                      <i className="fas fa-plane-departure me-2"></i>
                      Departure Flights
                    </h6>
                    <div className="p-3 bg-light rounded">
                      <Row>
                        {bookingData.umrah_package.departure_flights.map((flight, idx) => (
                          <Col md={6} key={`dep-via-${idx}`} className="mb-3">
                            <div className="p-3 border rounded h-100" style={{ backgroundColor: 'white' }}>
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <h6 className="mb-0 text-primary">
                                  DEPARTURE - VIA {idx + 1}
                                </h6>
                                <small className="text-muted">
                                  {getAirlineName(flight)} - {flight.flight_number}
                                </small>
                              </div>
                              
                              <div className="mb-2">
                                <div className="d-flex align-items-center">
                                  <i className="fas fa-plane-departure text-info me-2"></i>
                                  <div>
                                    <strong>{flight.departure_airport}</strong>
                                    <br />
                                    <small className="text-muted">
                                      {formatDate(flight.departure_date)} {formatTime(flight.departure_time)}
                                    </small>
                                  </div>
                                </div>
                                
                                <div className="d-flex align-items-center my-2">
                                  <div className="me-2" style={{ width: '24px' }}></div>
                                  <div className="text-center">
                                    <i className="fas fa-arrow-down text-muted"></i>
                                    <small className="text-muted d-block">to</small>
                                  </div>
                                </div>
                                
                                <div className="d-flex align-items-center">
                                  <i className="fas fa-plane-arrival text-success me-2"></i>
                                  <div>
                                    <strong>{flight.arrival_airport}</strong>
                                    <br />
                                    <small className="text-muted">
                                      {formatDate(flight.arrival_date)} {formatTime(flight.arrival_time)}
                                    </small>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="border-top pt-2">
                                <div className="row">
                                  {flight.departure_terminal && (
                                    <div className="col-6">
                                      <small className="text-muted">Departure Terminal:</small><br />
                                      <strong>{flight.departure_terminal}</strong>
                                    </div>
                                  )}
                                  {flight.arrival_terminal && (
                                    <div className="col-6">
                                      <small className="text-muted">Arrival Terminal:</small><br />
                                      <strong>{flight.arrival_terminal}</strong>
                                    </div>
                                  )}
                                </div>
                                
                                <div className="row mt-2">
                                  {flight.baggage_allowance && (
                                    <div className="col-6">
                                      <small className="text-muted">Baggage:</small><br />
                                      <strong>{flight.baggage_allowance}</strong>
                                    </div>
                                  )}
                                  {flight.hand_carry && (
                                    <div className="col-6">
                                      <small className="text-muted">Hand Carry:</small><br />
                                      <strong>{flight.hand_carry}</strong>
                                    </div>
                                  )}
                                </div>
                                
                                {flight.meals_included && (
                                  <div className="row mt-2">
                                    <div className="col-12">
                                      <div className="d-flex align-items-center">
                                        <i className="fas fa-utensils text-success me-2"></i>
                                        <small className="text-success fontweight600">Meals Included</small>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  </div>
                )}

                {/* Via Flights - Return Flights */}
                {isViaFlight() && bookingData?.umrah_package?.return_flights && bookingData.umrah_package.return_flights.length > 0 && (
                  <div className="mb-4">
                    <h6 className="text-warning mb-3">
                      <i className="fas fa-plane-arrival me-2"></i>
                      Return Flights
                    </h6>
                    <div className="p-3 bg-light rounded">
                      <Row>
                        {bookingData.umrah_package.return_flights.map((flight, idx) => (
                          <Col md={6} key={`ret-via-${idx}`} className="mb-3">
                            <div className="p-3 border rounded h-100" style={{ backgroundColor: 'white' }}>
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <h6 className="mb-0 text-primary">
                                  RETURN - VIA {idx + 1}
                                </h6>
                                <small className="text-muted">
                                  {getAirlineName(flight)} {flight.flight_number}
                                </small>
                              </div>
                              
                              <div className="mb-2">
                                <div className="d-flex align-items-center">
                                  <i className="fas fa-plane-departure text-info me-2"></i>
                                  <div>
                                    <strong>{flight.departure_airport}</strong>
                                    <br />
                                    <small className="text-muted">
                                      {formatDate(flight.departure_date)} {formatTime(flight.departure_time)}
                                    </small>
                                  </div>
                                </div>
                                
                                <div className="d-flex align-items-center my-2">
                                  <div className="me-2" style={{ width: '24px' }}></div>
                                  <div className="text-center">
                                    <i className="fas fa-arrow-down text-muted"></i>
                                    <small className="text-muted d-block">to</small>
                                  </div>
                                </div>
                                
                                <div className="d-flex align-items-center">
                                  <i className="fas fa-plane-arrival text-success me-2"></i>
                                  <div>
                                    <strong>{flight.arrival_airport}</strong>
                                    <br />
                                    <small className="text-muted">
                                      {formatDate(flight.arrival_date)} {formatTime(flight.arrival_time)}
                                    </small>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="border-top pt-2">
                                <div className="row">
                                  {flight.departure_terminal && (
                                    <div className="col-6">
                                      <small className="text-muted">Departure Terminal:</small><br />
                                      <strong>{flight.departure_terminal}</strong>
                                    </div>
                                  )}
                                  {flight.arrival_terminal && (
                                    <div className="col-6">
                                      <small className="text-muted">Arrival Terminal:</small><br />
                                      <strong>{flight.arrival_terminal}</strong>
                                    </div>
                                  )}
                                </div>
                                
                                <div className="row mt-2">
                                  {flight.baggage_allowance && (
                                    <div className="col-6">
                                      <small className="text-muted">Baggage:</small><br />
                                      <strong>{flight.baggage_allowance}</strong>
                                    </div>
                                  )}
                                  {flight.hand_carry && (
                                    <div className="col-6">
                                      <small className="text-muted">Hand Carry:</small><br />
                                      <strong>{flight.hand_carry}</strong>
                                    </div>
                                  )}
                                </div>
                                
                                {flight.meals_included && (
                                  <div className="row mt-2">
                                    <div className="col-12">
                                      <div className="d-flex align-items-center">
                                        <i className="fas fa-utensils text-success me-2"></i>
                                        <small className="text-success fontweight600">Meals Included</small>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  </div>
                )}

                {/* Final Fallback - Show if no flights found */}
                {(!bookingData?.umrah_package?.departure_flights || bookingData.umrah_package.departure_flights.length === 0) && 
                 (!bookingData?.umrah_package?.return_flights || bookingData.umrah_package.return_flights.length === 0) && (
                  <div className="text-center text-muted">
                    <i className="fas fa-plane fa-3x mb-3"></i>
                    <p>No flight information available</p>
                    <small>Package ID: {bookingData?.umrah_package?.id}</small>
                  </div>
                )}

              </Card.Body>
            </Card>

            {/* 5. Hotel Information Section */}
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Hotel Information</h5>
              </Card.Header>
              <Card.Body>
                {bookingData?.umrah_package?.hotels && bookingData.umrah_package.hotels.length > 0 ? (
                  <Row>
                    {bookingData.umrah_package.hotels.map((hotel, idx) => (
                      <Col md={6} key={idx} className="mb-3">
                        <Card className="h-100">
                          <Card.Header className="bg-light">
                            <h6 className="mb-0 text-dark">
                              {hotel.location}
                            </h6>
                          </Card.Header>
                          <Card.Body>
                            <h6 className="text-dark mb-2">{hotel.hotel_name}</h6>
                            {hotel.category && (
                              <p><strong>Category:</strong> <Badge bg="secondary">{hotel.category}</Badge></p>
                            )}
                            {hotel.star_rating && (
                              <p><strong>Rating:</strong> 
                                {[...Array(parseInt(hotel.star_rating))].map((_, i) => (
                                  <i key={i} className="fas fa-star text-warning ms-1"></i>
                                ))}
                                <span className="ms-1">({hotel.star_rating} Star)</span>
                              </p>
                            )}
                            {hotel.distance && (
                              <p><strong>Distance from Haram:</strong> {hotel.distance}</p>
                            )}
                            {hotel.address && (
                              <p><strong>Address:</strong> {hotel.address}</p>
                            )}
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <div className="text-center text-muted">
                    <i className="fas fa-hotel fa-3x mb-3"></i>
                    <p>No hotel information available</p>
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* 6. Package Features Section */}
            <Card className="mb-4">
              <Card.Header>
                <h5 className="mb-0">Package Features</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={12}>
                    <div className="d-flex flex-wrap gap-2">
                      {bookingData?.umrah_package?.non_refundable === 1 && (
                        <Badge bg="warning" className="px-3 py-2">
                          <i className="fas fa-ban me-1"></i>Non-Refundable
                        </Badge>
                      )}
                      {bookingData?.umrah_package?.zayarat_included === 1 && (
                        <Badge bg="warning" className="px-3 py-2">
                          <i className="fas fa-mosque me-1"></i>Zayarat Included
                        </Badge>
                      )}
                      {bookingData?.umrah_package?.non_changeable === 1 && (
                        <Badge bg="danger" className="px-3 py-2">
                          <i className="fas fa-ban me-1"></i>Non Changeable
                        </Badge>
                      )}
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>

      {/* Modals */}
      {showTime && token.role === 'admin' && (
        <UmrahTimeUpdate show={showTime} setShow={setShowTime} selectedBooking={targetBooking} />
      )}
      
      {showConfirm && token.role === 'admin' && (
        <UmrahConfirmBooking show={showConfirm} setShow={setShowConfirm} selectedBooking={targetBooking} />
      )}
      
      {showPNR && token.role === 'admin' && (
        <UmrahUpdatePNR show={showPNR} setShow={setShowPNR} selectedBooking={targetBooking} />
      )}
      
      {showDiscount && token.role === 'admin' && (
        <UmrahAdditionalDiscount 
          show={showDiscount} 
          setShow={setShowDiscount} 
          selectedBooking={targetBooking}
          onSuccess={() => {
            // Refresh booking data after discount changes
            setIsLoading(true);
            axios.get(`${API_BASE_URL}/umrah-bookings/${id}`, {
              headers: {
                'Authorization': `Bearer ${getBearerToken()}`
              }
            })
              .then(response => {
                setIsLoading(false);
                setBookingData(response?.data?.data)
              }).catch(error => {
                console.error(error);
                setIsLoading(false);
                toast.error('Failed to refresh booking details');
              });
          }}
        />
      )}
      
      {showCustomPricing && token.role === 'admin' && (
        <UmrahCustomPricing 
          show={showCustomPricing} 
          setShow={setShowCustomPricing} 
          selectedBooking={targetBooking}
          onSuccess={() => {
            // Refresh booking data after custom pricing changes
            setIsLoading(true);
            axios.get(`${API_BASE_URL}/umrah-bookings/${id}`, {
              headers: {
                'Authorization': `Bearer ${getBearerToken()}`
              }
            })
              .then(response => {
                setIsLoading(false);
                setBookingData(response?.data?.data)
              }).catch(error => {
                console.error(error);
                setIsLoading(false);
                toast.error('Failed to refresh booking details');
              });
          }}
        />
      )}
      
      {confirmation.target === 'cancel' && (
        <ConfirmationPopup 
          show={confirmation} 
          setShow={setConfirmation} 
          handleConfirm={handleConfirmAction}
          message="Are you sure you want to cancel this Umrah booking?"
        />
      )}
      
      {showEdit && (
        <BookUmrah 
          show={showEdit} 
          setShow={setShowEdit} 
          selectedPackage={bookingData?.umrah_package}
          existingBooking={targetBooking}
          onSuccess={() => {
            // Refresh booking data after edit
            setIsLoading(true);
            axios.get(`${API_BASE_URL}/umrah-bookings/${id}`, {
              headers: {
                'Authorization': `Bearer ${getBearerToken()}`
              }
            })
              .then(response => {
                setIsLoading(false);
                setBookingData(response?.data?.data)
                toast.success('Booking updated successfully!');
              }).catch(error => {
                console.error(error);
                setIsLoading(false);
                toast.error('Failed to refresh booking details');
              });
          }}
        />
      )}
    </>
  )
}

export default UmrahBookingDetail